import {
  Box,
  type BoxProps,
  CardHeader,
  type CardHeaderProps,
  Flex,
  type FlexProps,
  Text,
} from '@chakra-ui/react';
import type { PropsWithChildren, ReactNode, RefObject } from 'react';

import type { PrimaryFilterId } from '../../filters/types/filters';
import type { DataProviderSet } from '../DataProvider/types';
import type {
  PositionedItem,
  PositionedItemInfo,
} from '../PageConfigProvider/types/item';
import {
  type MetricValues,
  metricOptions,
} from '../PageConfigProvider/types/metric';
import { ChartNotSupported } from './ChartNotSupported';
import { ChartSelector } from './ChartRoot/ChartSelector';
import {
  isSpecialChartType,
  SpecialChartSelector,
} from './ChartRoot/SpecialChart';
import { ChartActionMenuWrapper } from './chart-menu/ChartActionMenuWrapper';
import { MetricSwitcher } from './chart-switcher/MetricSwitcher';
import { MultiPrimaryEntitySwitcher } from './chart-switcher/MultiPrimaryEntitySwitcher';
import { PrimaryEntitySwitcher } from './chart-switcher/PrimaryEntitySwitcher';
import { SecondaryDimensionSwitcher } from './chart-switcher/SecondaryDimensionSwitcher';
import { PlotInfoComp } from './PlotInfoComp';

/** ================================
 * Chart Header
 ================================ */
type ChartHeaderProps = PropsWithChildren<
  CardHeaderProps & {
    headerSize?: 'sm' | 'md';
  }
>;

export const ChartHeader = ({
  headerSize = 'md',
  children,
  ...cardHeaderProps
}: ChartHeaderProps) => {
  const horizontalPadding = headerSize === 'md' ? '16px' : '14px';
  const topPadding = headerSize === 'md' ? '12px' : '8px';
  const padding = `${topPadding} ${horizontalPadding} 0 ${horizontalPadding}`;
  return (
    <CardHeader
      display="flex"
      flexWrap="nowrap"
      justifyContent="space-between"
      alignItems="center"
      borderTopLeftRadius="md"
      borderTopRightRadius="md"
      background="white"
      transition="background 0.1s ease-in-out"
      gap="10px"
      width="100%"
      maxWidth="100%"
      position="relative"
      color="#2d426a"
      fontSize={headerSize === 'sm' ? '0.75rem' : '0.875rem'}
      fontWeight="600"
      lineHeight="24px"
      role="heading"
      padding={padding}
      {...cardHeaderProps}
    >
      {children}
    </CardHeader>
  );
};

/** ================================
 * Chart Header Title
 ================================ */
export const ChartHeaderTitle = ({
  children,
  ...boxProps
}: PropsWithChildren<BoxProps>) => {
  return (
    <Box
      overflow="hidden"
      maxWidth="100%"
      display="flex"
      alignItems="center"
      gap="0.625rem"
      {...boxProps}
    >
      {children}
    </Box>
  );
};

type ChartHeaderTitleInfoWrapperProps = PropsWithChildren<
  FlexProps & {
    chartTitleInfo?: ReactNode;
  }
>;

export const ChartHeaderTitleInfoWrapper = ({
  chartTitleInfo,
  children,
  ...flexProps
}: ChartHeaderTitleInfoWrapperProps) => {
  return (
    <Flex overflow="hidden" {...flexProps}>
      {children}
      {chartTitleInfo}
    </Flex>
  );
};

export const ChartTitleInfo = ({ info }: { info: PositionedItemInfo }) => {
  const { text, hyperlinks } = info;
  if (hyperlinks && hyperlinks.length > 0) {
    const parts = text.split('<a>');
    return (
      <PlotInfoComp
        plotInfoConfig={{
          popoverPlacement: 'right',
          iconBoxSize: 2.5,
        }}
      >
        <Text color="white">
          {parts.map((part, idx) => (
            <span key={idx}>
              {part}
              {idx < hyperlinks.length && (
                <Text
                  as="span"
                  color="green.300"
                  _hover={{ textDecoration: 'underline' }}
                  display="inline"
                >
                  <a
                    href={hyperlinks?.[idx]?.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {hyperlinks?.[idx]?.text}
                  </a>
                </Text>
              )}
            </span>
          ))}
        </Text>
      </PlotInfoComp>
    );
  }

  return (
    <PlotInfoComp
      plotInfoConfig={{
        popoverPlacement: 'right',
        iconBoxSize: 2.5,
      }}
    >
      <Text color="white" display="inline">
        {text}
      </Text>
    </PlotInfoComp>
  );
};

export const ChartHeaderTitleInfo = ({
  title,
  info,
}: {
  title: string;
  info?: PositionedItemInfo;
}) => {
  return (
    <ChartHeaderTitleInfoWrapper
      {...(info && {
        chartTitleInfo: <ChartTitleInfo info={info} />,
      })}
    >
      <ChartHeaderTitle>
        <Text
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          maxWidth="100%"
          data-chart-title
        >
          {title}
        </Text>
      </ChartHeaderTitle>
    </ChartHeaderTitleInfoWrapper>
  );
};

/** ================================
 * Chart Action Menu
 ================================ */
export const ChartHeaderAction = ({ children }: PropsWithChildren) => {
  return (
    <Flex alignItems="center" justifyContent="flex-end" width="100%">
      {children}
    </Flex>
  );
};

type ChartActionMenuProps = {
  item: PositionedItem;
  metric?: MetricValues;
  title: string;
  primaryFilterId?: PrimaryFilterId;
  slim: boolean;
  printRef: RefObject<HTMLDivElement>;
  data?: unknown;
  dataType?: string;
  modalContent?: ReactNode;
};

export const ChartActionMenu = ({
  item,
  metric,
  title,
  primaryFilterId,
  slim,
  printRef,
  data,
  dataType,
  modalContent,
}: ChartActionMenuProps) => {
  const { defaultMetricOptions, secondaryDimension, chartType } =
    item?.plot ?? {};

  const metricSwitcherOptions = defaultMetricOptions
    ? metricOptions.filter((option) =>
        defaultMetricOptions.includes(option.value)
      )
    : null;
  const hasSubfilter =
    metric === 'share_of_headcount' || (metricSwitcherOptions?.length ?? 0) > 1;

  const expandedModalContent = (() => {
    if (modalContent) return modalContent;
    if (chartType && isSpecialChartType(chartType)) {
      return <SpecialChartSelector />;
    }
    if (chartType && primaryFilterId && data) {
      return (
        <ChartSelector
          chartType={chartType}
          data={data as DataProviderSet}
          item={item}
          metric={metric}
          secondaryDimension={secondaryDimension}
        />
      );
    }
    return <ChartNotSupported />;
  })();

  return (
    <ChartActionMenuWrapper
      title={((): string => {
        if (hasSubfilter) {
          const metricLabel = metricOptions.find(
            (m) => m.value === metric
          )?.label;
          return metricLabel
            ? `${title || 'Chart'} (${metricLabel})`
            : title || 'Chart';
        }
        return title || 'Chart';
      })()}
      primaryFilterId={primaryFilterId}
      secondaryDimension={secondaryDimension}
      metric={metric}
      slim={slim}
      menu={[
        'expand-plot',
        'share-plot',
        'download-data',
        'download-png',
        'download-ppt',
      ]}
      printRef={printRef}
      chartId={item.id}
      expandModalContent={expandedModalContent}
      data={data}
      dataType={dataType}
      plotChartType={chartType}
    />
  );
};

/** ================================
 * Chart Switchers
 ================================ */
type ChartSwitchersProps = FlexProps & {
  primaryFilterId?: PrimaryFilterId;
  isSingleEntityChart: boolean;
  showMultiPrimaryEntitySwitcher?: boolean;
};

export const ChartSwitchers = ({
  primaryFilterId,
  isSingleEntityChart,
  showMultiPrimaryEntitySwitcher,
  ...flexProps
}: ChartSwitchersProps) => {
  return (
    <Flex gap="2" alignItems="center" width="100%" {...flexProps}>
      <MetricSwitcher />
      {primaryFilterId && (
        <PrimaryEntitySwitcher
          isSingleEntityChart={isSingleEntityChart}
          primaryFilterId={primaryFilterId}
        />
      )}
      {showMultiPrimaryEntitySwitcher && primaryFilterId && (
        <MultiPrimaryEntitySwitcher primaryFilterId={primaryFilterId} />
      )}
      <SecondaryDimensionSwitcher />
    </Flex>
  );
};
